import type { ScopeType } from '@tabita/shared-kernel';

/**
 * The system role catalog.
 *
 * These are seeded defaults, not hard-coded behaviour. No business logic
 * anywhere in TABITA may branch on a role key — code asks the authorization
 * service for a permission. Tenants may clone, edit and extend these roles.
 */
export interface SystemRoleDefinition {
  readonly key: string;
  readonly nameKey: string;
  readonly descriptionKey: string;
  readonly defaultScopeType: ScopeType;
  /** True for the TABITA operator role, which is never granted inside a tenant. */
  readonly isPlatformRole: boolean;
  /** Permission keys, or a `module.resource.*` wildcard expanded at seed time. */
  readonly permissions: readonly string[];
}

export const SYSTEM_ROLES: readonly SystemRoleDefinition[] = [
  {
    key: 'PLATFORM_SUPER_ADMIN',
    nameKey: 'roles.platform_super_admin.name',
    descriptionKey: 'roles.platform_super_admin.description',
    defaultScopeType: 'TENANT',
    isPlatformRole: true,
    // Deliberately narrow: operating the platform is not the same as reading
    // church data. Access to tenant records requires audited impersonation.
    permissions: ['platform.*', 'audit.*', 'module.installation.*', 'entitlement.*'],
  },
  {
    key: 'CHURCH_OWNER',
    nameKey: 'roles.church_owner.name',
    descriptionKey: 'roles.church_owner.description',
    defaultScopeType: 'TENANT',
    isPlatformRole: false,
    permissions: [
      'tenancy.*',
      'authorization.*',
      'identity.*',
      'entitlement.*',
      'module.installation.*',
      'audit.*',
      'people.*',
      'groups.*',
      'events.*',
      'forms.*',
      'attendance.*',
      'academic.*',
      'kids.*',
      'giving.*',
      'finance.*',
      'billing.*',
      'notification.*',
      'communications.*',
      'calendar.*',
      'inventory.*',
      'file.*',
      'search.*',
    ],
  },
  {
    key: 'CHURCH_ADMINISTRATOR',
    nameKey: 'roles.church_administrator.name',
    descriptionKey: 'roles.church_administrator.description',
    defaultScopeType: 'TENANT',
    isPlatformRole: false,
    permissions: [
      'tenancy.tenant.read',
      'tenancy.tenant.admin',
      'tenancy.organization.read',
      'tenancy.campus.*',
      'tenancy.unit.*',
      'tenancy.membership.*',
      'tenancy.setting.*',
      'tenancy.pastor.*',
      'authorization.role.read',
      'authorization.role.assign',
      'authorization.permission.read',
      'people.*',
      'groups.*',
      'events.*',
      'forms.*',
      'attendance.*',
      'academic.*',
      'kids.*',
      'giving.*',
      'finance.*',
      'billing.*',
      'notification.*',
      'communications.*',
      'calendar.*',
      'inventory.*',
      'file.*',
      'search.*',
      'audit.event.read',
    ],
  },
  {
    key: 'EXECUTIVE_PASTOR',
    nameKey: 'roles.executive_pastor.name',
    descriptionKey: 'roles.executive_pastor.description',
    defaultScopeType: 'ORGANIZATION',
    isPlatformRole: false,
    permissions: [
      'tenancy.organization.read',
      'tenancy.campus.read',
      'tenancy.unit.read',
      'people.person.read',
      'people.person.create',
      'people.person.update',
      'people.household.*',
      'people.membership.*',
      'people.relationship.*',
      'people.note.*',
      'people.tag.*',
      'people.customField.*',
      'people.directory.read',
      'groups.group.*',
      'groups.member.manage',
      'events.event.*',
      'events.registration.manage',
      'forms.form.*',
      'forms.submission.*',
      'attendance.record.*',
      'calendar.calendar.read',
      'calendar.event.read',
      'search.global.read',
    ],
  },
  {
    key: 'PASTOR',
    nameKey: 'roles.pastor.name',
    descriptionKey: 'roles.pastor.description',
    defaultScopeType: 'CAMPUS',
    isPlatformRole: false,
    permissions: [
      'tenancy.campus.read',
      'tenancy.unit.read',
      'people.person.read',
      'people.person.create',
      'people.person.update',
      'people.household.read',
      'people.household.update',
      'people.membership.*',
      'people.relationship.read',
      'people.note.*',
      'people.customField.read',
      'people.customField.update',
      'people.directory.read',
      'groups.group.*',
      'groups.member.manage',
      'events.event.*',
      'events.registration.manage',
      'forms.form.*',
      'forms.submission.*',
      'attendance.record.*',
      'calendar.calendar.read',
      'calendar.event.read',
      'search.global.read',
    ],
  },
  {
    key: 'MINISTRY_DIRECTOR',
    nameKey: 'roles.ministry_director.name',
    descriptionKey: 'roles.ministry_director.description',
    defaultScopeType: 'UNIT',
    isPlatformRole: false,
    permissions: [
      'tenancy.unit.read',
      'people.person.read',
      'people.person.update',
      'people.household.read',
      'people.tag.read',
      'people.directory.read',
      'groups.group.read',
      'groups.member.manage',
      'events.event.read',
      'events.registration.manage',
      'search.global.read',
    ],
  },
  {
    key: 'GROUP_LEADER',
    nameKey: 'roles.group_leader.name',
    descriptionKey: 'roles.group_leader.description',
    defaultScopeType: 'GROUP',
    isPlatformRole: false,
    /*
     * Read-only on groups for now, despite the role's name.
     *
     * `defaultScopeType: 'GROUP'` is the right model — a leader manages their
     * own roster and no one else's — but nothing resolves GROUP scope yet:
     * `resolveCampusScope` understands TENANT, ORGANIZATION and CAMPUS, and
     * defaults to deny for anything else. Granting `groups.member.manage` here
     * would therefore either do nothing or, if the resolver were later widened
     * carelessly, hand every group in the church to every leader. Read is
     * useful and safe; the rest waits for scope resolution to catch up.
     */
    permissions: ['people.person.read', 'people.directory.read', 'groups.group.read'],
  },
  {
    key: 'VOLUNTEER_COORDINATOR',
    nameKey: 'roles.volunteer_coordinator.name',
    descriptionKey: 'roles.volunteer_coordinator.description',
    defaultScopeType: 'UNIT',
    isPlatformRole: false,
    permissions: [
      'people.person.read',
      'people.directory.read',
      'groups.group.read',
      'events.event.read',
      'events.registration.manage',
      'attendance.record.read',
      'calendar.calendar.read',
      'calendar.event.read',
      'search.global.read',
    ],
  },
  {
    key: 'WORSHIP_LEADER',
    nameKey: 'roles.worship_leader.name',
    descriptionKey: 'roles.worship_leader.description',
    defaultScopeType: 'CAMPUS',
    isPlatformRole: false,
    // Enough to see what equipment exists and check it out for a service —
    // not to manage the catalog itself, which stays with FACILITY_MANAGER.
    permissions: [
      'people.person.read',
      'people.directory.read',
      'inventory.item.read',
      'inventory.category.read',
      'inventory.location.read',
      'inventory.checkout.read',
      'inventory.checkout.manage',
    ],
  },
  {
    key: 'KIDS_DIRECTOR',
    nameKey: 'roles.kids_director.name',
    descriptionKey: 'roles.kids_director.description',
    defaultScopeType: 'CAMPUS',
    isPlatformRole: false,
    permissions: [
      'people.person.read',
      'people.household.read',
      'people.relationship.read',
      'kids.checkin.read',
    ],
  },
  {
    key: 'KIDS_CHECKIN_OPERATOR',
    nameKey: 'roles.kids_checkin_operator.name',
    descriptionKey: 'roles.kids_checkin_operator.description',
    defaultScopeType: 'CAMPUS',
    isPlatformRole: false,
    // No directory read and no export: an operator identifies a child at the
    // desk, they do not browse or extract the child roster.
    permissions: [
      'people.person.read',
      'attendance.record.create',
      'attendance.record.read',
      'kids.checkin.create',
      'kids.checkin.read',
      'kids.checkin.update',
      'calendar.calendar.read',
      'calendar.event.read',
    ],
  },
  {
    key: 'FINANCE_MANAGER',
    nameKey: 'roles.finance_manager.name',
    descriptionKey: 'roles.finance_manager.description',
    defaultScopeType: 'ORGANIZATION',
    isPlatformRole: false,
    permissions: [
      'people.person.read',
      'people.directory.read',
      'giving.donation.read',
      'giving.donation.create',
      'giving.donation.update',
      'giving.donation.refund',
      'finance.account.read',
      'finance.journal_entry.read',
      'finance.journal_entry.create',
      'billing.subscription.read',
    ],
  },
  {
    key: 'ACCOUNTANT',
    nameKey: 'roles.accountant.name',
    descriptionKey: 'roles.accountant.description',
    defaultScopeType: 'ORGANIZATION',
    isPlatformRole: false,
    permissions: [
      'people.person.read',
      'giving.donation.read',
      'finance.account.read',
      'finance.journal_entry.read',
      'billing.subscription.read',
    ],
  },
  {
    key: 'COMMUNICATIONS_MANAGER',
    nameKey: 'roles.communications_manager.name',
    descriptionKey: 'roles.communications_manager.description',
    defaultScopeType: 'ORGANIZATION',
    isPlatformRole: false,
    permissions: [
      'people.person.read',
      'people.tag.*',
      'people.directory.read',
      'forms.form.*',
      'forms.submission.*',
      'attendance.record.*',
      'notification.log.read',
      'search.global.read',
      'communications.campaign.*',
      'calendar.calendar.read',
      'calendar.event.read',
    ],
  },
  {
    key: 'EVENT_MANAGER',
    nameKey: 'roles.event_manager.name',
    descriptionKey: 'roles.event_manager.description',
    defaultScopeType: 'CAMPUS',
    isPlatformRole: false,
    permissions: [
      'people.person.read',
      'people.directory.read',
      'events.event.*',
      'events.registration.manage',
      'search.global.read',
    ],
  },
  {
    key: 'FACILITY_MANAGER',
    nameKey: 'roles.facility_manager.name',
    descriptionKey: 'roles.facility_manager.description',
    defaultScopeType: 'CAMPUS',
    isPlatformRole: false,
    // The role Inventory was built for: full run of the module, scoped to the
    // manager's own campus the same way every other CAMPUS-scoped role is.
    permissions: ['tenancy.campus.read', 'inventory.*'],
  },
  {
    key: 'TEACHER',
    nameKey: 'roles.teacher.name',
    descriptionKey: 'roles.teacher.description',
    defaultScopeType: 'GROUP',
    isPlatformRole: false,
    permissions: ['people.person.read'],
  },
  {
    key: 'VOLUNTEER',
    nameKey: 'roles.volunteer.name',
    descriptionKey: 'roles.volunteer.description',
    defaultScopeType: 'OWN',
    isPlatformRole: false,
    permissions: ['people.person.read'],
  },
  {
    key: 'MEMBER',
    nameKey: 'roles.member.name',
    descriptionKey: 'roles.member.description',
    defaultScopeType: 'OWN',
    isPlatformRole: false,
    permissions: [
      'people.person.read',
      'people.person.update',
      'people.household.read',
      // Auto-matrícula and requesting a homologation — the two academic
      // actions a member takes on themselves, not staff-only management.
      'academic.enrollment.self',
      'academic.homologation.create',
    ],
  },
  {
    key: 'PORTAL_USER',
    nameKey: 'roles.portal_user.name',
    descriptionKey: 'roles.portal_user.description',
    defaultScopeType: 'OWN',
    isPlatformRole: false,
    permissions: ['people.person.read', 'people.person.update'],
  },
  {
    key: 'GUEST',
    nameKey: 'roles.guest.name',
    descriptionKey: 'roles.guest.description',
    defaultScopeType: 'OWN',
    isPlatformRole: false,
    permissions: [],
  },
];

export const SYSTEM_ROLE_KEYS: readonly string[] = SYSTEM_ROLES.map((role) => role.key);

export function findSystemRole(key: string): SystemRoleDefinition | undefined {
  return SYSTEM_ROLES.find((role) => role.key === key);
}
